import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Loader2,
  AlertCircle,
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface EarningRecord {
  id: number;
  booking_id: number;
  service_name: string;
  category_name?: string;
  customer_name: string;
  amount_gbp: string;
  percentage: number;
  status: 'pending' | 'completed' | 'failed';
  booking_status: string;
  created_at: string;
  updated_at: string;
}

interface EarningsSummary {
  total_earned_gbp: string;
  completed_gbp: string;
  pending_gbp: string;
  this_month_gbp: string;
  total_jobs: number;
}

interface WeeklyPoint {
  label: string;
  amount: number;
}

const ProviderEarningsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [earnings, setEarnings] = useState<EarningRecord[]>([]);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [weeklyData, setWeeklyData] = useState<WeeklyPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [chartMode, setChartMode] = useState<'week' | 'month'>('week');
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchEarnings();
    }
  }, [user?.id, authLoading]);

  useEffect(() => {
    if (earnings.length > 0) {
      setWeeklyData(buildChartData(earnings, chartMode));
    }
  }, [earnings, chartMode]);

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/provider/earnings');
      if (response?.data) {
        if (Array.isArray(response.data.earnings)) {
          setEarnings(response.data.earnings);
        }
        if (response.data.summary) {
          setSummary(response.data.summary);
        }
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load earnings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const buildChartData = (records: EarningRecord[], mode: 'week' | 'month'): WeeklyPoint[] => {
    const now = new Date();
    if (mode === 'week') {
      const buckets: Record<string, number> = {};
      for (let i = 7; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i * 7);
        const wn = getWeekNum(d);
        const label = `Wk ${wn}`;
        buckets[label] = 0;
      }
      records.forEach((r) => {
        const d = new Date(r.created_at);
        if (isNaN(d.getTime())) return;
        const diffWeeks = Math.floor((now.getTime() - d.getTime()) / (7 * 86400000));
        if (diffWeeks >= 0 && diffWeeks <= 7) {
          const label = `Wk ${getWeekNum(d)}`;
          if (label in buckets) {
            buckets[label] += parseFloat(r.amount_gbp || '0');
          }
        }
      });
      return Object.entries(buckets).map(([label, amount]) => ({ label, amount: Math.round(amount * 100) / 100 }));
    } else {
      const buckets: Record<string, number> = {};
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const label = d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
        buckets[label] = 0;
      }
      records.forEach((r) => {
        const d = new Date(r.created_at);
        if (isNaN(d.getTime())) return;
        const label = d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
        if (label in buckets) {
          buckets[label] += parseFloat(r.amount_gbp || '0');
        }
      });
      return Object.entries(buckets).map(([label, amount]) => ({ label, amount: Math.round(amount * 100) / 100 }));
    }
  };

  const getWeekNum = (d: Date) => {
    const onejan = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7);
  };

  const handleDownloadStatement = async () => {
    setDownloading(true);
    try {
      const response = await apiService.get('/provider/earnings/statement/download');
      if (response?.url) {
        window.open(response.url, '_blank');
      } else if (response?.data) {
        // Fallback: build CSV from earnings data
        const rows = [
          ['Job ID', 'Service', 'Customer', 'Date', 'Amount ($)', 'Commission (%)', 'Status'],
          ...earnings.map((e) => [
            `#${e.booking_id}`,
            e.service_name || '—',
            e.customer_name,
            new Date(e.created_at).toLocaleDateString('en-GB'),
            parseFloat(e.amount_gbp || '0').toFixed(2),
            `${e.percentage || 0}%`,
            e.status,
          ]),
        ];
        const csv = rows.map((r) => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `earnings-statement-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: 'Downloaded', description: 'Earnings statement exported as CSV' });
      } else {
        // CSV fallback when no response data
        const rows = [
          ['Job ID', 'Service', 'Customer', 'Date', 'Amount ($)', 'Status'],
          ...earnings.map((e) => [
            `#${e.booking_id}`,
            e.service_name || '—',
            e.customer_name,
            new Date(e.created_at).toLocaleDateString('en-GB'),
            parseFloat(e.amount_gbp || '0').toFixed(2),
            e.status,
          ]),
        ];
        const csv = rows.map((r) => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `earnings-statement-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: 'Downloaded', description: 'Earnings statement exported as CSV' });
      }
    } catch {
      // Generate CSV from current data
      if (earnings.length === 0) {
        toast({ title: 'No data', description: 'No earnings to export yet', variant: 'destructive' });
        return;
      }
      const rows = [
        ['Job ID', 'Service', 'Customer', 'Date', 'Amount ($)', 'Status'],
        ...earnings.map((e) => [
          `#${e.booking_id}`,
          e.service_name || '—',
          e.customer_name,
          new Date(e.created_at).toLocaleDateString('en-GB'),
          parseFloat(e.amount_gbp || '0').toFixed(2),
          e.status,
        ]),
      ];
      const csv = rows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earnings-statement-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Downloaded', description: 'Earnings statement exported as CSV' });
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (val: string | number | undefined) => {
    const n = parseFloat(String(val || 0));
    return isNaN(n) ? '0.00' : n.toLocaleString('en-GB', { minimumFractionDigits: 2 });
  };

  const statusBadge = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-700';
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Earnings - VireWire Provider</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Earnings</h1>
            <p className="text-gray-600 mt-1 text-sm">Track your income, platform fees, and payout history.</p>
          </div>
          <Button
            onClick={handleDownloadStatement}
            disabled={downloading || earnings.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {downloading ? 'Exporting...' : 'Download Statement'}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">This Month</p>
                <TrendingUp className="w-4 h-4 text-blue-500" />
              </div>
              {loading ? (
                <div className="h-8 bg-gray-100 animate-pulse rounded" />
              ) : (
                <p className="text-3xl font-bold text-gray-900">£{fmt(summary?.this_month_gbp)}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                {summary?.total_jobs ?? 0} jobs total
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Pending Release</p>
                <Clock className="w-4 h-4 text-yellow-500" />
              </div>
              {loading ? (
                <div className="h-8 bg-gray-100 animate-pulse rounded" />
              ) : (
                <p className="text-3xl font-bold text-gray-900">£{fmt(summary?.pending_gbp)}</p>
              )}
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500">Awaiting processing</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-6 px-2"
                  onClick={() => navigate('/provider/payouts')}
                >
                  Request
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Completed</p>
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              {loading ? (
                <div className="h-8 bg-gray-100 animate-pulse rounded" />
              ) : (
                <p className="text-3xl font-bold text-green-700">£{fmt(summary?.completed_gbp)}</p>
              )}
              <Button
                size="sm"
                className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white text-xs h-7"
                onClick={() => navigate('/provider/payouts')}
              >
                View Payouts
              </Button>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Lifetime Earnings</p>
                <DollarSign className="w-4 h-4 text-purple-500" />
              </div>
              {loading ? (
                <div className="h-8 bg-gray-100 animate-pulse rounded" />
              ) : (
                <p className="text-3xl font-bold text-purple-700">£{fmt(summary?.total_earned_gbp)}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">All-time net earnings</p>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Earnings Trend</h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={chartMode === 'week' ? 'default' : 'outline'}
                  onClick={() => setChartMode('week')}
                  className={chartMode === 'week' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  By Week
                </Button>
                <Button
                  size="sm"
                  variant={chartMode === 'month' ? 'default' : 'outline'}
                  onClick={() => setChartMode('month')}
                  className={chartMode === 'month' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  By Month
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="h-48 bg-gray-50 animate-pulse rounded-lg" />
            ) : weeklyData.some((d) => d.amount > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weeklyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'Net Earnings']}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="amount" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 bg-gray-50 rounded-lg flex flex-col items-center justify-center gap-2">
                <TrendingUp className="w-8 h-8 text-gray-300" />
                <p className="text-sm text-gray-400">No earnings data for this period</p>
                <p className="text-xs text-gray-400">Complete jobs to start earning</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Earnings Table */}
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-base font-bold text-gray-900">Earnings History</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />
                ))}
              </div>
            ) : earnings.length === 0 ? (
              <div className="py-12 text-center">
                <DollarSign className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500">No earnings records yet</p>
                <p className="text-gray-400 text-sm mt-1">Completed jobs will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Job ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Service</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Customer</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Date</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Amount</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Commission</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.map((e) => (
                      <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 font-semibold text-gray-900">#{e.booking_id}</td>
                        <td className="py-3 px-4 text-gray-700">{e.service_name || '—'}</td>
                        <td className="py-3 px-4 text-gray-600">{e.customer_name}</td>
                        <td className="py-3 px-4 text-gray-500 text-xs">
                          {new Date(e.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-gray-900">
                          ${fmt(e.amount_gbp)}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-500 text-xs">
                          {e.percentage ? `${e.percentage}%` : '—'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={`text-xs ${statusBadge(e.status)}`}>
                            {e.status.charAt(0).toUpperCase() + e.status.slice(1)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td colSpan={4} className="py-3 px-4 text-xs font-bold text-gray-600 uppercase">Total</td>
                      <td className="py-3 px-4 text-right font-bold text-gray-900">
                        ${fmt(summary?.total_earned_gbp)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ProviderLayout>
  );
};

export default ProviderEarningsPage;
